'use client'

import * as React from 'react'
import { Tooltip as TooltipPrimitive } from 'radix-ui'

import { cn } from '../utils'
import { useMediaQuery } from '../hooks/use-media-query'

function TooltipProvider({
  delayDuration = 180, // Slight delay to account for quick mouse movement over elements (same as ember app)
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot='tooltip-provider'
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot='tooltip' {...props} />
    </TooltipProvider>
  )
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot='tooltip-trigger' {...props} />
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot='tooltip-content'
        sideOffset={sideOffset}
        className={cn(
          'bg-popover-foreground text-popover animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className='bg-popover-foreground fill-popover-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]' />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

/** Tailwind breakpoints a label can collapse below. */
type CollapseBreakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

/**
 * Matches the Tailwind default breakpoints — there are no `--breakpoint-*`
 * overrides in `globals.css`, so these must be kept in sync with Tailwind's
 * defaults rather than read from the theme.
 */
const COLLAPSE_QUERIES: Record<CollapseBreakpoint, string> = {
  sm: '(width < 40rem)',
  md: '(width < 48rem)',
  lg: '(width < 64rem)',
  xl: '(width < 80rem)',
  '2xl': '(width < 96rem)',
}

/**
 * Static per-breakpoint class strings — Tailwind cannot generate class names
 * from runtime values, so each variant has to appear literally in the source.
 *
 * `sr-only` (not `hidden`) keeps the label in the accessibility tree while it is
 * visually collapsed, so the control retains its accessible name and needs no
 * `aria-label`.
 */
const COLLAPSED_LABEL_CLASSES: Record<CollapseBreakpoint, string> = {
  sm: 'sr-only sm:not-sr-only',
  md: 'sr-only md:not-sr-only',
  lg: 'sr-only lg:not-sr-only',
  xl: 'sr-only xl:not-sr-only',
  '2xl': 'sr-only 2xl:not-sr-only',
}

const CollapseBreakpointContext = React.createContext<CollapseBreakpoint | undefined>(undefined)

type CollapsibleGroupState = {
  /** Labels with an `order` at or below this are collapsed; 0 means none are. */
  collapsedThrough: number
  /** Re-measure from fully expanded, after the group's content has changed. */
  remeasure: () => void
}

const CollapsibleGroupContext = React.createContext<CollapsibleGroupState | undefined>(undefined)
const CollapseOrderContext = React.createContext<number | undefined>(undefined)

/**
 * A container that collapses nested `CollapsibleLabel`s with an `order` only
 * when its content overflows — the lowest `order` first, then the next one up
 * if it still doesn't fit. There are no widths to tune, so it adapts to buttons
 * hidden behind feature flags and to longer translations.
 *
 * Once every label is collapsed and the content still overflows, the group
 * sets `data-exhausted` so children can give up space of their own (e.g.
 * `in-data-exhausted:min-w-32`); it is cleared when the width recovers.
 *
 * The container's width must come from its parent rather than its content, and
 * the controls inside it must not shrink (`shrink-0`) — overflow is what
 * triggers a collapse.
 */
function CollapsibleGroup({ className, ...props }: React.ComponentProps<'div'>) {
  const ref = React.useRef<HTMLDivElement>(null)
  const [collapsedThrough, setCollapsedThrough] = React.useState(0)
  const collapsedThroughRef = React.useRef(0)
  // Every label is collapsed and the content still overflows
  const [isExhausted, setIsExhausted] = React.useState(false)
  const isExhaustedRef = React.useRef(false)
  // Content width each level needed when it overflowed — the container has to
  // be at least this wide before that level can be expanded back to
  const requiredWidths = React.useRef(new Map<number, number>())

  const collapseTo = React.useCallback((level: number) => {
    collapsedThroughRef.current = level
    setCollapsedThrough(level)
  }, [])

  const exhaust = React.useCallback((value: boolean) => {
    isExhaustedRef.current = value
    setIsExhausted(value)
  }, [])

  const measure = React.useCallback(() => {
    const element = ref.current
    if (!element) return

    const current = collapsedThroughRef.current
    const orders = Array.from(
      element.querySelectorAll<HTMLElement>('[data-collapse-order]'),
      label => Number(label.dataset.collapseOrder),
    )

    if (element.scrollWidth > element.clientWidth) {
      const next = Math.min(...orders.filter(order => order > current))
      if (!Number.isFinite(next)) {
        // Only record the first overflow — once exhausted, the shrunken children
        // report a smaller width than the one needed to restore them
        if (!isExhaustedRef.current) {
          requiredWidths.current.set(current, element.scrollWidth)
          exhaust(true)
        }
        return
      }
      requiredWidths.current.set(current, element.scrollWidth)
      collapseTo(next)
      return
    }

    if (isExhaustedRef.current) {
      if (element.clientWidth >= (requiredWidths.current.get(current) ?? Infinity)) exhaust(false)
      return
    }

    if (current === 0) return
    const previous = Math.max(0, ...orders.filter(order => order < current))
    if (element.clientWidth >= (requiredWidths.current.get(previous) ?? Infinity)) {
      collapseTo(previous)
    }
  }, [collapseTo, exhaust])

  const remeasure = React.useCallback(() => {
    requiredWidths.current.clear()
    exhaust(false)
    if (collapsedThroughRef.current === 0) measure()
    else collapseTo(0)
  }, [measure, collapseTo, exhaust])

  // Each level change re-measures, so collapsing cascades before paint
  React.useLayoutEffect(measure, [measure, collapsedThrough, isExhausted])

  React.useEffect(() => {
    const element = ref.current
    if (!element || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => measure())
    observer.observe(element)
    return () => observer.disconnect()
  }, [measure])

  const value = React.useMemo(
    () => ({ collapsedThrough, remeasure }),
    [collapsedThrough, remeasure],
  )

  return (
    <CollapsibleGroupContext.Provider value={value}>
      <div
        data-slot='collapsible-group'
        data-exhausted={isExhausted ? '' : undefined}
        className={className}
        {...props}
        ref={ref}
      />
    </CollapsibleGroupContext.Provider>
  )
}

/** Whether a label with this `order` is collapsed by its enclosing `CollapsibleGroup`. */
function useIsCollapsedInGroup(order: number | undefined) {
  const group = React.useContext(CollapsibleGroupContext)
  return order !== undefined && !!group && order <= group.collapsedThrough
}

type CollapsibleLabelProps = React.ComponentProps<'span'> & {
  /**
   * Breakpoint to collapse below. Defaults to the `collapseBelow` of the
   * enclosing `TooltipElement`, so the breakpoint is declared in one place.
   */
  below?: CollapseBreakpoint
  /**
   * Collapse when the enclosing `CollapsibleGroup` overflows, lowest order
   * first. Defaults to the `collapseOrder` of the enclosing `TooltipElement`.
   * Takes precedence over `below`.
   */
  order?: number
}

/**
 * A label that is visible at wide viewports and visually collapses to
 * screen-reader-only below a breakpoint, leaving an icon-only control.
 *
 * Inside a `TooltipElement` with `collapseBelow` set, the tooltip takes over as
 * the visible label exactly when this collapses.
 */
function CollapsibleLabel({ below, order, className, ...props }: CollapsibleLabelProps) {
  const inheritedBreakpoint = React.useContext(CollapseBreakpointContext)
  const inheritedOrder = React.useContext(CollapseOrderContext)
  const remeasure = React.useContext(CollapsibleGroupContext)?.remeasure
  const collapseOrder = order ?? inheritedOrder
  const isGrouped = collapseOrder !== undefined && !!remeasure
  const isCollapsedInGroup = useIsCollapsedInGroup(collapseOrder)
  const breakpoint = isGrouped ? undefined : (below ?? inheritedBreakpoint)

  // Labels appearing, disappearing or changing text change the width the group needs
  React.useLayoutEffect(() => {
    if (!isGrouped) return
    remeasure()
    return () => {
      requestAnimationFrame(remeasure)
    }
  }, [isGrouped, remeasure, props.children])

  return (
    <span
      data-slot='collapsible-label'
      data-collapse-order={isGrouped ? collapseOrder : undefined}
      className={cn(
        breakpoint && COLLAPSED_LABEL_CLASSES[breakpoint],
        isCollapsedInGroup && 'sr-only',
        className,
      )}
      {...props}
    />
  )
}

type TooltipElementProps = {
  text: React.ReactNode
  children: React.ReactElement
  sideOffset?: number
  className?: string
  /**
   * Only show the tooltip below this breakpoint, and collapse any nested
   * `CollapsibleLabel` at the same point — so the tooltip appears only when the
   * label it duplicates is no longer visible, never alongside it.
   *
   * When set, the tooltip content is `aria-hidden` because the collapsed label
   * already provides the accessible name and Radix would otherwise announce the
   * same text again via `aria-describedby`.
   */
  collapseBelow?: CollapseBreakpoint
  /**
   * Like `collapseBelow`, but collapses with the enclosing `CollapsibleGroup`
   * when it overflows instead of at a viewport breakpoint. Lower orders
   * collapse first. Set this or `collapseBelow`, not both.
   */
  collapseOrder?: number
  ariaHidden?: boolean
}

const TooltipElement = ({
  text,
  children,
  sideOffset = 0,
  className,
  collapseBelow,
  collapseOrder,
  ariaHidden = false,
}: TooltipElementProps) => {
  const isBelowBreakpoint = useMediaQuery(collapseBelow && COLLAPSE_QUERIES[collapseBelow])
  const isCollapsedInGroup = useIsCollapsedInGroup(collapseOrder)
  const isCollapsible = !!collapseBelow || collapseOrder !== undefined
  const isCollapsed = collapseBelow ? isBelowBreakpoint : isCollapsedInGroup

  return (
    <CollapseBreakpointContext.Provider value={collapseBelow}>
      <CollapseOrderContext.Provider value={collapseOrder}>
        <Tooltip open={isCollapsible ? (isCollapsed ? undefined : false) : undefined}>
          <TooltipTrigger asChild>{children}</TooltipTrigger>
          {text && (
            <TooltipContent sideOffset={sideOffset} className={className}>
              {isCollapsible || ariaHidden ? <span aria-hidden='true'>{text}</span> : text}
            </TooltipContent>
          )}
        </Tooltip>
      </CollapseOrderContext.Provider>
    </CollapseBreakpointContext.Provider>
  )
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipElement,
  CollapsibleLabel,
  CollapsibleGroup,
}
export type { CollapseBreakpoint }
